/**
 * Content priority system.
 *
 * The preferred display order for core content:
 *   1. Panadería Cero
 *   2. Repostería Cero
 *   3. Cocina Real
 *   4. Clean Sweets
 *   5. Clean Meals
 */

/** Hotmart product IDs mapped to preferred display order */
const CORE_PRIORITY_ORDER: Record<string, number> = {
  // Panadería Cero / Clean Bakes
  "7222660": 1,
  "7418452": 1,
  "7456362": 1,
  "8061842": 1,
  // Repostería Cero / Clean Sweets
  "7368845": 2,
  "7381203": 2,
  // Cocina Real / Clean Meals
  "7339562": 3,
  "7368977": 3,
  "7381661": 3,
  // GlucoPhoto
  "7330458": 4,
  "7468894": 4,
  "7487694": 4,
  // German market products
  "7778364": 5, // Kuchenglück
  "7778710": 6, // Brotglück
  "7778879": 7, // Kochglück
};

/** Check if a hotmart_product_id belongs to a core (non-PDF) product */
export function isCoreProduct(hotmartProductId: string | null | undefined): boolean {
  if (!hotmartProductId) return false;
  return hotmartProductId in CORE_PRIORITY_ORDER;
}

/** Get the preferred display position for a core product (lower = higher priority) */
export function getCorePriority(hotmartProductId: string | null | undefined): number {
  if (!hotmartProductId) return 999;
  return CORE_PRIORITY_ORDER[hotmartProductId] ?? 999;
}

/**
 * Unified content item used for display across Home and "Todos los Contenidos".
 */
export interface UnifiedContentItem {
  id: string;
  name: string;
  hotmartProductId: string | null;
  type: "core";
  /** The recipe module id */
  sourceId: string;
  /** Image URL */
  image?: string | null;
  /** Checkout URL */
  checkoutUrl?: string | null;
  /** Module data for paywall */
  moduleData?: any;
}

/**
 * Sort unified content items in preferred order.
 */
export function sortContentByPriority(items: UnifiedContentItem[]): UnifiedContentItem[] {
  return [...items].sort((a, b) => getCorePriority(a.hotmartProductId) - getCorePriority(b.hotmartProductId));
}

/**
 * Split and sort content for display.
 * All content is now free — everything goes into `owned`.
 */
export function splitAndSortContent(
  items: UnifiedContentItem[],
  _hasProduct: (productId: string) => boolean,
): { owned: UnifiedContentItem[]; locked: UnifiedContentItem[] } {
  return {
    owned: sortContentByPriority(items),
    locked: [],
  };
}
